import { useEffect, useState } from 'react';

export interface ChartThemeColors {
  accent: string;
  accentMuted: string;
  text: string;
  textMuted: string;
  grid: string;
  surface: string;
  border: string;
  // Palette para múltiplas séries (linhas, donut, etc.)
  palette: string[];
  success: string;
  warning: string;
  danger: string;
}

/**
 * Lê cores tematizadas via CSS variables, reagindo automaticamente a qualquer
 * mudança no atributo `data-theme` do <html>.
 *
 * Usa MutationObserver em vez do ThemeContext porque o botão "Exportar PDF"
 * altera `data-theme` direto no DOM (sem passar pelo contexto) pra forçar
 * tema claro durante a impressão. Sem o observer, o Recharts ficaria com as
 * cores do tema anterior congeladas no SVG.
 */
export function useThemeColors(): ChartThemeColors {
  const [colors, setColors] = useState<ChartThemeColors>(() => readColors());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const html = document.documentElement;

    const refresh = () => {
      // requestAnimationFrame garante que o navegador já recalculou as
      // CSS vars antes de lermos getComputedStyle.
      window.requestAnimationFrame(() => setColors(readColors()));
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'data-theme') {
          refresh();
          break;
        }
      }
    });
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });

    return () => observer.disconnect();
  }, []);

  return colors;
}

function readColors(): ChartThemeColors {
  if (typeof window === 'undefined') {
    return fallback();
  }
  const style = getComputedStyle(document.documentElement);
  const get = (name: string, def: string) => {
    const v = style.getPropertyValue(name).trim();
    return v || def;
  };

  const accent = get('--accent', '#FCB428');
  const text = get('--text-primary', '#FFFFFF');
  const textMuted = get('--text-muted', '#94A3B8');
  const border = get('--border', '#003550');
  const surface = get('--bg-card', '#002538');
  const success = get('--success', '#22C55E');
  const warning = get('--warning', '#F59E0B');
  const danger = get('--danger', '#EF4444');

  return {
    accent,
    accentMuted: get('--accent-hover', accent),
    text,
    textMuted,
    grid: border,
    surface,
    border,
    success,
    warning,
    danger,
    palette: [accent, '#7ED6DD', '#A78BFA', '#22C55E', '#F59E0B', '#EF2525'],
  };
}

function fallback(): ChartThemeColors {
  return {
    accent: '#FCB428',
    accentMuted: '#FFC94D',
    text: '#FFFFFF',
    textMuted: '#94A3B8',
    grid: '#003550',
    surface: '#002538',
    border: '#003550',
    success: '#22C55E',
    warning: '#F59E0B',
    danger: '#EF4444',
    palette: ['#FCB428', '#7ED6DD', '#A78BFA', '#22C55E', '#F59E0B', '#EF2525'],
  };
}
